import React, { useEffect, useState } from 'react';
import { clsx } from 'clsx';

interface TimeIndicatorProps {
  hourHeight: number; // The height in pixels of one hour in the grid (e.g., 60 or 80)
  topOffset?: number; // 🌟 NEW: Add topOffset prop
  /**
   * 🛡️ Hybrid Scope Props
   * Used to adjust visual cues based on the active data partition.
   */
  activeScope?: 'PERSONAL' | 'HOUSEHOLD' | 'BOTH';
  isPersonalMode?: boolean; // True if currentHouseholdId is null
}

export const TimeIndicator: React.FC<TimeIndicatorProps> = ({
  hourHeight,
  topOffset = 0, // 🌟 NEW: Destructure and set default
  activeScope = 'BOTH',
  isPersonalMode = false
}) => {
  const [now, setNow] = useState(new Date());

  // Update the line position every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  // Calculate position: (Hours * height) + (Minutes / 60 * height)
  const topPosition = topOffset + (now.getHours() * hourHeight) + (now.getMinutes() / 60 * hourHeight);

  // 🎨 Contextual Branding: Emerald for Personal, Indigo for Shared/Both
  const isPersonalActive = isPersonalMode || activeScope === 'PERSONAL';

  return (
    <div 
      className="absolute left-0 right-0 z-30 pointer-events-none flex items-center transition-all duration-700 ease-in-out"
      style={{ top: `${topPosition}px` }}
    >
      {/* 🛡️ The Pulsing Dot (Anchor) */}
      <div className="relative flex h-3 w-3 items-center justify-center -ml-1.5">
        <span className={clsx(
          "absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping",
          isPersonalActive ? "bg-emerald-400" : "bg-indigo-400"
        )}></span>
        <span className={clsx(
          "relative inline-flex rounded-full h-2 w-2 shadow-lg",
          isPersonalActive ? "bg-emerald-600 shadow-emerald-500/60" : "bg-indigo-600 shadow-indigo-500/60"
        )}></span>
      </div>

      {/* 🚀 The Glowing Pulse Line */}
      <div className={clsx(
        "flex-1 h-px relative overflow-hidden transition-colors duration-500",
        isPersonalActive 
          ? "bg-gradient-to-r from-emerald-500/50 via-emerald-500/20 to-transparent" 
          : "bg-gradient-to-r from-indigo-500/50 via-indigo-500/20 to-transparent"
      )}>
        {/* Animated Sweep Effect */}
        <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full animate-[shimmer_3s_infinite]" />
      </div>

      {/* Digital Time Badge - Provides high-fidelity temporal feedback */}
      <span className={clsx(
        "absolute right-4 text-[9px] font-black uppercase tracking-tighter bg-white dark:bg-slate-950 px-1.5 py-0.5 rounded transition-colors",
        isPersonalActive ? "text-emerald-500/70" : "text-indigo-500/70"
      )}>
        {now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </span>
    </div>
  );
};